import { FirebaseApp, initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  Auth,
  UserCredential,
  updateEmail,
  updatePassword,
  User,
} from "firebase/auth";
import {
  getAnalytics,
  setUserId,
  logEvent,
  isSupported,
} from "firebase/analytics";
import { getDatabase } from "firebase/database";
import Cookies from "../utilities/cookies";
import Database from "../utilities/database";
import ConfigCache, { FEATURE_FLAG_GHOST_USER } from "../utilities/ConfigCache";
import { CookieShape, IShowNotificationState, IUserRecord, ControlRecord } from "../ts/Definitions";
import { NotificationType } from "../controllers/messaging";

export class AuthenticationService {
  private fbAuth!: Auth;
  private app!: FirebaseApp;
  private database!: Database;
  private configCache!: ConfigCache;
  private localMode: boolean;
  private showMessage: (args: IShowNotificationState) => void = () => ({});

  constructor(localMode: boolean = false) {
    this.localMode = localMode;
  }

  /**
   * Initializes Firebase services (app, auth, database)
   */
  public initializeFirebase(dbInstance?: Database): void {
    let firebaseConfig = {};
    if (
      import.meta.env.MODE === "staging" ||
      window.location.hostname === "tarlings-dev.web.app"
    ) {
      // For Firebase JS SDK v7.20.0 and later, measurementId is optional
      firebaseConfig = {
        apiKey: "AIzaSyBKFWern0IrBrNrVbNlqAnO696iPZtXR1A",
        authDomain: "tarlings-dev.firebaseapp.com",
        databaseURL:
          "https://tarlings-dev-default-rtdb.europe-west1.firebasedatabase.app",
        projectId: "tarlings-dev",
        storageBucket: "tarlings-dev.appspot.com",
        messagingSenderId: "743167069988",
        appId: "1:743167069988:web:68ef7b553cd3054f99edb1",
        measurementId: "G-VD2CT1MRL7",
      };
    } else {
      firebaseConfig = {
        apiKey: "AIzaSyCbVvKpENs1zPXxh-fVfIxHp_XpWADbYXk",
        authDomain: "tarlingsstore.firebaseapp.com",
        databaseURL: "https://tarlingsstore.firebaseio.com",
        projectId: "tarlingsstore",
        storageBucket: "tarlingsstore.appspot.com",
        messagingSenderId: "515857777464",
        appId: "1:515857777464:web:cc5367dea6fe77166437cd",
        measurementId: "G-C0X8DEX6Y7",
      };
    }

    this.app = initializeApp(firebaseConfig);

    const isProduction = !(
      import.meta.env.MODE === "staging" ||
      window.location.hostname === "tarlings-dev.web.app"
    );

    if (isProduction) {
      const siteKey = import.meta.env.VITE_SITE_KEY;
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = false;
      try {
        initializeAppCheck(this.app, {
          provider: new ReCaptchaV3Provider(siteKey as string),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (error) {
        console.error("Failed to initialize AppCheck:", error);
      }
    } else {
      (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = false;
    }

    this.fbAuth = getAuth(this.app);
    this.database = dbInstance || new Database(getDatabase(this.app), this.localMode);
  }

  public setConfigCache(configCache: ConfigCache): void {
    this.configCache = configCache;
  }

  public setShowMessage(showMessage: (args: IShowNotificationState) => void): void {
    this.showMessage = showMessage;
  }

  public getDatabase(): Database {
    return this.database;
  }

  public getAuth(): Auth {
    return this.fbAuth;
  }

  /**
   * Helper to construct Firebase email from username
   */
  private constructFirebaseEmail(username: string): string {
    return `${username.replace(" ", "")}@tarlings.com`;
  }

  /**
   * Helper to handle Firebase authentication errors consistently
   */
  private handleAuthError(error: unknown): void {
    const e = error instanceof Error ? error : new Error(String(error));
    this.showMessage({
      message: e.message,
      persist: true,
      notificationType: NotificationType.ErrorInfo,
    });
  }

  /**
   * Helper to finalize user login after authentication
   */
  public async finalizeUserLogin(
    username: string,
    password: string,
    userid: string,
    autoLogon: boolean,
    isNewUser: boolean,
    cookiesService: Cookies
  ): Promise<{ success: boolean; userRecord?: ControlRecord }> {
    await this.database.initialise();

    let match = await this.database.lookupUserByUsername(username);
    if (!match && isNewUser) {
      const sysSettings = await this.database.getSystemSettings();
      cookiesService.setUser(password, username, userid, true, autoLogon);
      match = await this.database.createUser(userid, username, {
        ...sysSettings,
      });
    } else if (match) {
      cookiesService.setUser(
        password,
        match.username,
        match.userid,
        true,
        autoLogon
      );
    }

    if (match) {
      return { success: true, userRecord: new ControlRecord(match) };
    }

    return { success: false };
  }

  /**
   * Logs existing user into the system
   */
  public async logExistingUserOn(
    creds: CookieShape,
    alias: string | undefined,
    cookiesService: Cookies
  ): Promise<{ success: boolean; userRecord?: ControlRecord }> {
    if (!creds.password || !creds.username) return { success: false };
    let userid: string | null = null;

    if (import.meta.env.VITE_LOCAL_MODE === 'true') {
      const allUsers = await this.database.getUsers();
      if (allUsers)
        userid = Object.values(allUsers).find(rec => rec.username === creds.username)?.userid ?? null;
    } else {
      try {
        const userCredential = await signInWithEmailAndPassword(
          this.fbAuth,
          creds.tempEmail || this.constructFirebaseEmail(creds.username),
          creds.password
        )
          .then(function (userCredential: UserCredential) {
            return userCredential;
          })
          .catch(function (error: { code: string; message: string }) {
            if (error.code === "auth/user-not-found") {
              return undefined;
            }
            throw error;
          });
        if (userCredential) {
          userid = userCredential.user?.uid || null;
          if (creds.tempEmail) {
            const userObj = this.fbAuth.currentUser;
            if (userObj)
              await updateEmail(
                userObj,
                this.constructFirebaseEmail(creds.username)
              );
            cookiesService.clearState("email");
          }
        }
      } catch (error) {
        this.handleAuthError(error);
      }
    }

    if (!userid) return { success: false };

    if (
      alias &&
      (await this.configCache
        .fetchConfig()
        .then(() =>
          this.configCache.allowedFeature(
            FEATURE_FLAG_GHOST_USER,
            creds.username,
            false
          )
        ))
    )
      creds.username = alias;

    return this.finalizeUserLogin(creds.username, creds.password, userid, creds.autoLogon ?? false, false, cookiesService);
  }

  /**
   * Registers a new user
   */
  public async registerNewUser(
    username: string,
    password: string,
    autoLogon: boolean,
    cookiesService: Cookies
  ): Promise<{ success: boolean; userRecord?: ControlRecord }> {
    if (!username || !password) return { success: false };
    let userid: string | null = null;
    const api = this.fbAuth;

    try {
      const userCredential = await signInWithEmailAndPassword(
        api,
        this.constructFirebaseEmail(username),
        password
      )
        .then((credentials) => {
          this.showMessage({
            message:
              "This username and password already exists, signed in as existing user",
            persist: true,
            notificationType: NotificationType.ErrorInfo,
          });
          return credentials;
        })
        .catch(async (error: { code: string; message: string }) => {
          if (error.code === "auth/user-not-found") {
            try {
              return await createUserWithEmailAndPassword(
                api,
                this.constructFirebaseEmail(username),
                password
              ).then(function (userCredential: UserCredential) {
                return userCredential;
              });
            } catch (error) {
              this.handleAuthError(error);
              return undefined;
            }
          } else {
            this.showMessage({
              message:
                "This username already exists and passwords do not match",
              persist: true,
              notificationType: NotificationType.ErrorInfo,
            });
          }
          return undefined;
        });
      if (userCredential) userid = userCredential.user?.uid || null;
    } catch (error) {
      this.handleAuthError(error);
    }

    if (!userid) return { success: false };

    return this.finalizeUserLogin(username, password, userid, autoLogon, true, cookiesService);
  }

  /**
   * Resets user password to default
   */
  public resetPassword(user: IUserRecord, onSuccess: () => void, onError: (userMail: string) => void): void {
    const userMail = this.constructFirebaseEmail(user.username);
    void createUserWithEmailAndPassword(this.fbAuth, userMail, "pa55w0rd")
      .then((userCredential: UserCredential) => {
        void this.database.reallocateUserId(user, userCredential).catch((error) =>
          console.error("Failed to reallocate user ID:", error)
        );
        onSuccess();
      })
      .catch(() => {
        onError(userMail);
      });
  }

  /**
   * Updates user password
   */
  public async updatePassword(newPassword: string): Promise<void> {
    await updatePassword(this.fbAuth.currentUser as User, newPassword);
  }

  /**
   * Updates user email address
   */
  public async updateEmailAddress(newUserName: string): Promise<void> {
    if (!newUserName?.trim()) throw new Error("Username is required");
    const emailAddress = this.constructFirebaseEmail(newUserName);
    await updateEmail(this.fbAuth.currentUser as User, emailAddress);
  }

  /**
   * Logs analytics event
   */
  public async logAnalyticsEvent(username: string): Promise<void> {
    if (import.meta.env.PROD && (await isSupported())) {
      const service = getAnalytics();
      setUserId(service, username);
      logEvent(service, "LogIn", {
        username,
        app_version: import.meta.env.VITE_VERSION,
      });
    }
  }
}
